import React, { useMemo } from 'react';
import {
  FiArrowLeft,
  FiBriefcase,
  FiMail,
  FiMapPin,
  FiPhone,
  FiRefreshCw,
  FiSearch,
  FiUsers
} from 'react-icons/fi';

export const formatEmployeeId = (value) => {
  if (!value) return '—';
  const clean = value.toString().trim();
  const match = clean.match(/^([A-Za-z]+)?\s*:?(\d+)$/);
  if (match) {
    const prefix = (match[1] || 'EMP').toUpperCase();
    const digits = match[2].replace(/^0+/, '') || '0';
    return `${prefix}${digits.padStart(3, '0')}`;
  }
  return clean || '—';
};

const DEFAULT_AVATAR =
  'https://www.pikpng.com/pngl/m/154-1540525_male-user-filled-icon-my-profile-icon-png.png';

const PeopleDirectory = ({
  users = [],
  loading,
  search,
  setSearch,
  onRefresh,
  onSelect,
  selected,
  onBack
}) => {
  const filtered = useMemo(() => {
    if (!search) return users;
    const value = search.toLowerCase();
    return users.filter((user) => {
      const empId = user.employeeId || user.employeeCode || '';
      return (
        user.name?.toLowerCase().includes(value) ||
        user.email?.toLowerCase().includes(value) ||
        user.department?.toLowerCase().includes(value) ||
        (empId && empId.toString().toLowerCase().includes(value)) ||
        (empId && formatEmployeeId(empId).toLowerCase().includes(value))
      );
    });
  }, [users, search]);

  const departments = useMemo(() => {
    const set = new Set();
    users.forEach((user) => {
      const dept = String(user.department || user.departmentName || '').trim();
      if (dept) set.add(dept);
    });
    return set.size;
  }, [users]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-teal-600 mb-1">
            People & Teams
          </p>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1a365d] flex items-center gap-2">
            <FiUsers className="text-teal-600" />
            All Teammates
          </h1>
          <p className="text-slate-600 mt-1 text-sm">
            Browse employees, search by name or ID, and view profile details.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 flex items-center gap-2 text-sm"
          >
            <FiArrowLeft /> Back
          </button>
          <button
            type="button"
            onClick={onRefresh}
            className="px-4 py-2 rounded-lg bg-[#1a365d] text-white hover:bg-[#153050] flex items-center gap-2 text-sm"
          >
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Total People</p>
          <p className="text-2xl font-bold text-[#1a365d] mt-1">{users.length}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Departments</p>
          <p className="text-2xl font-bold text-teal-700 mt-1">{departments}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm col-span-2 md:col-span-1">
          <p className="text-xs text-slate-500 uppercase tracking-wide">Search Results</p>
          <p className="text-2xl font-bold text-slate-700 mt-1">{filtered.length}</p>
        </div>
      </div>

      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name, email, department, or employee ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none text-sm"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
            <h2 className="font-semibold text-slate-800 text-sm">Directory</h2>
          </div>
          <div className="max-h-[560px] overflow-y-auto p-3 space-y-2">
            {loading ? (
              <div className="py-12 text-center text-slate-500 text-sm">Loading teammates…</div>
            ) : filtered.length === 0 ? (
              <div className="py-12 text-center text-slate-500 text-sm">
                No teammates match &ldquo;{search}&rdquo;.
              </div>
            ) : (
              filtered.map((user) => {
                const active = selected?._id === user._id;
                return (
                  <button
                    type="button"
                    key={user._id}
                    onClick={() => onSelect(user)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-all ${
                      active
                        ? 'border-teal-400 bg-teal-50 shadow-sm'
                        : 'border-slate-100 bg-slate-50 hover:bg-white hover:border-slate-200'
                    }`}
                  >
                    <img
                      src={user.profilePic || DEFAULT_AVATAR}
                      alt={user.name}
                      className="w-11 h-11 rounded-xl object-cover shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 truncate">{user.name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {user.position || '—'} · {user.department || '—'}
                      </p>
                      <p className="text-[10px] font-bold tracking-wider text-teal-700 mt-0.5">
                        {formatEmployeeId(user.employeeId || user.employeeCode)}
                      </p>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {!selected ? (
            <div className="h-full min-h-[320px] flex items-center justify-center text-slate-400 text-sm p-8 text-center">
              Select a teammate to view their profile.
            </div>
          ) : (
            <div className="p-5 md:p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start pb-5 border-b border-slate-100">
                <img
                  src={selected.profilePic || DEFAULT_AVATAR}
                  alt={selected.name}
                  className="w-20 h-20 rounded-2xl object-cover shrink-0"
                />
                <div>
                  <h3 className="text-xl font-bold text-[#1a365d]">{selected.name}</h3>
                  <p className="text-slate-600">{selected.position || '—'}</p>
                  <span className="inline-block mt-2 px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 text-xs font-bold tracking-wide">
                    {formatEmployeeId(selected.employeeId || selected.employeeCode)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                <section>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1">
                    <FiMail className="text-teal-600" /> Contact
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-slate-500">Email:</span>{' '}
                      <span className="text-slate-800">{selected.email || '—'}</span>
                    </p>
                    <p className="flex items-center gap-1">
                      <FiPhone className="text-slate-400 shrink-0" />
                      <span>{selected.phone || '—'}</span>
                    </p>
                  </div>
                </section>

                <section>
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1">
                    <FiBriefcase className="text-teal-600" /> Company
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p>
                      <span className="text-slate-500">Company:</span>{' '}
                      {selected.company || '—'}
                    </p>
                    <p>
                      <span className="text-slate-500">Department:</span>{' '}
                      {selected.department || '—'}
                    </p>
                    <p>
                      <span className="text-slate-500">Role:</span> {selected.role || '—'}
                    </p>
                    <p>
                      <span className="text-slate-500">Joined:</span>{' '}
                      {selected.dateOfJoining
                        ? new Date(selected.dateOfJoining).toLocaleDateString('en-IN')
                        : '—'}
                    </p>
                  </div>
                </section>
              </div>

              {selected.rolesAndResponsibility?.length > 0 ? (
                <section className="mt-5 pt-5 border-t border-slate-100">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">
                    Responsibilities
                  </h4>
                  <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
                    {selected.rolesAndResponsibility.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </section>
              ) : null}

              {(selected.city || selected.state || selected.country) && (
                <section className="mt-5 pt-5 border-t border-slate-100">
                  <h4 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1">
                    <FiMapPin className="text-teal-600" /> Location
                  </h4>
                  <p className="text-sm text-slate-700">
                    {[selected.city, selected.state, selected.country].filter(Boolean).join(', ') ||
                      '—'}
                  </p>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PeopleDirectory;
